import { WINDOW_MS } from '@iris/qr-token';
import { type LastEvent, systemClock, unwrap } from '@iris/shared';
import { useAuth } from '@iris/ui/auth/auth-provider';
import { useOnline } from '@iris/ui/hooks/use-online';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { idbStorage } from '../idb-storage';
import { fetchSeed, frameAt, readSeed, type StoredSeed } from './qr-seed';

/**
 * The stored seed of the signed-in customer (instant, offline), refreshed from the server whenever online so the
 * clock offset is the one "measured at the last online moment" (FR-QR-04). `undefined` = still reading.
 */
export function useQrSeed(): StoredSeed | null | undefined {
  const { api, user, status } = useAuth();
  const online = useOnline();
  const [seed, setSeed] = useState<StoredSeed | null | undefined>(undefined);
  const userId = user?.id;
  const memberCode = user?.memberCode;
  const firstName = user?.firstName ?? null;
  // offline cold start: no /me, but the device's seed belongs to the account signed in here
  const offlineBoot = !userId && status === 'offline';

  useEffect(() => {
    if (!userId && !offlineBoot) return;
    let active = true;
    void readSeed(idbStorage, userId).then((stored) => {
      if (active) setSeed((current) => current ?? stored);
    });
    if (online && userId && memberCode) {
      fetchSeed(
        async () => unwrap(await api.GET('/api/v1/me/qr-seed')),
        { memberCode, firstName },
        idbStorage,
        systemClock.now,
      )
        .then((fresh) => active && setSeed(fresh))
        .catch(() => undefined); // offline or session lost: the stored seed stays
    }
    return () => {
      active = false;
    };
  }, [api, userId, memberCode, firstName, online, offlineBoot]);

  return seed;
}

/** The token for the current server window, redrawn exactly at each boundary, plus the ring's progress. */
export function useRotatingToken(seed: StoredSeed | null | undefined) {
  const [frame, setFrame] = useState<{ token: string; endsAt: number } | null>(null);
  const [now, setNow] = useState(() => systemClock.now());
  const computing = useRef(false);

  useEffect(() => {
    if (!seed) return;
    let active = true;
    const refresh = async () => {
      computing.current = true;
      const at = systemClock.now();
      const next = await frameAt(seed, at);
      computing.current = false;
      if (active) setFrame({ token: next.token, endsAt: at + next.msLeft });
    };
    void refresh();
    const timer = window.setInterval(() => {
      const at = systemClock.now();
      setNow(at);
      setFrame((current) => {
        if (current && at >= current.endsAt && !computing.current) void refresh();
        return current;
      });
    }, 250);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [seed]);

  const msLeft = frame ? Math.max(frame.endsAt - now, 0) : 0;
  return {
    token: frame?.token ?? null,
    fractionLeft: msLeft / WINDOW_MS,
    secondsLeft: Math.ceil(msLeft / 1000),
  };
}

/** FR-QR-03: keep the screen on while My QR is open (Wake Lock API where available). */
export function useWakeLock(): void {
  useEffect(() => {
    const wakeLock = (
      navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinel> } }
    ).wakeLock;
    if (!wakeLock) return;
    let sentinel: WakeLockSentinel | null = null;
    let active = true;
    const acquire = () => {
      if (document.visibilityState !== 'visible') return;
      wakeLock
        .request('screen')
        .then((lock) => {
          if (active) sentinel = lock;
          else void lock.release();
        })
        .catch(() => undefined);
    };
    acquire();
    // the browser drops the lock when the page is hidden; take it again on return
    document.addEventListener('visibilitychange', acquire);
    return () => {
      active = false;
      document.removeEventListener('visibilitychange', acquire);
      void sentinel?.release();
    };
  }, []);
}

interface WakeLockSentinel {
  release(): Promise<void>;
}

/**
 * T4 §2.5: while My QR is visible and online, ask the API every 2 s whether a visit was just recorded (or voided),
 * so the result shows on the customer's phone even without push. `since` is in server time (stored offset).
 */
export function useLastEvent(seed: StoredSeed | null | undefined) {
  const { api } = useAuth();
  const online = useOnline();
  const queryClient = useQueryClient();
  const [event, setEvent] = useState<LastEvent | null>(null);
  const since = useRef<string | null>(null);

  useEffect(() => {
    if (!seed || !online) return;
    since.current ??= new Date(systemClock.now() + seed.offsetMs).toISOString();
    let active = true;
    let busy = false;
    const poll = async () => {
      if (busy || document.visibilityState !== 'visible' || !since.current) return;
      busy = true;
      try {
        const { data, response } = await api.GET('/api/v1/me/last-event', {
          params: { query: { since: since.current } },
        });
        if (active && response.status === 200 && data) {
          since.current = data.at;
          setEvent(data);
          void queryClient.invalidateQueries({ queryKey: ['wallet'] });
          void queryClient.invalidateQueries({ queryKey: ['card', data.merchantId] });
        }
      } catch {
        // offline for a moment or rate-limited: the next tick tries again
      } finally {
        busy = false;
      }
    };
    const timer = window.setInterval(() => void poll(), 2_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [api, online, queryClient, seed]);

  return { event, dismiss: () => setEvent(null) };
}
